import {
  HR_EMPLOYEES_PAGE_ROUTE,
  HR_IMPORT_WORKER_PAGE_ROUTE,
  HR_WORKER_DETAIL_PAGE_ROUTE,
  PROJECTS_VOLUNTEERS_PAGE_ROUTE,
  HR_HIRING_PAGE_ROUTE,
  HR_EXPERTS_PAGE_ROUTE,
  HR_CONTRACTS_PAGE_ROUTE,
  HR_LEAVES_PAGE_ROUTE,
  HR_FILL_CONTRACT_PAGE_ROUTE,
  HR_EDIT_CONTRACT_PAGE_ROUTE,
  LEGAL_TEMPLATES_PAGE_ROUTE,
  LEGAL_APPLICATIONS_PAGE_ROUTE,
  LEGAL_ATTACHED_DOCUMENTS_PAGE_ROUTE,
  ACCOUNTING_TIMESHEETS_PAGE_ROUTE,
  ACCOUNTING_TIMESHEET_DETAIL_PAGE_ROUTE,
  ACCOUNTING_CONTRACTOR_PAYROLL_PAGE_ROUTE,
  ACCOUNTING_CONTRACTOR_PAYROLL_DETAIL_PAGE_ROUTE,
  ACCOUNTING_PURCHASES_PAGE_ROUTE,
  ACCOUNTING_WAREHOUSE_REQUESTS_PAGE_ROUTE,
  ACCOUNTING_DEPRECIATION_PAGE_ROUTE,
  ACCOUNTING_FIXED_ASSETS_PAGE_ROUTE,
  ACCOUNTING_WAREHOUSE_PAGE_ROUTE,
  ACCOUNTING_TAX_ROUTE,
  ACCOUNTING_FINANCIAL_REPORTS_PAGE_ROUTE,
  ACCOUNTING_EQUITY_CHANGES_REPORT_PAGE_ROUTE,
  ACCOUNTING_REPORTS_PAGE_ROUTE,
  ACCOUNTING_INCOME_PAGE_ROUTE,
  FINANCE_PAGE_ROUTE,
  PROFILE_PAGE_ROUTE,
  REQUESTS_PAGE_ROUTE,
  REQUESTS_ACTIVE_PAGE_ROUTE,
  REQUESTS_HISTORY_PAGE_ROUTE,
  CALCULATIONS_PAGE_ROUTE,
  PENDING_ACTIONS_PAGE_ROUTE,
  SUPPORT_PAGE_ROUTE,
  PROJECTS_PAGE_ROUTE,
  PROJECT_MONITORING_ROUTE,
  PROJECT_REPORT_PREVIEW_ROUTE,
  PROJECT_REPORTS_ROUTE,
  ANNUAL_REPORT_PREVIEW_ROUTE,
  ANNUAL_REPORTS_ROUTE,
  ESG_REPORT_PREVIEW_ROUTE,
  ESG_REPORTS_ROUTE,
  SROI_PROJECT_ROUTE,
  SROI_ROUTE,
  PROJECT_DETAIL_ROUTE,
  BOARD_PAGE_ROUTE,
  FORMS_PAGE_ROUTE,
  CAMPAIGNS_PAGE_ROUTE,
  CAMPAIGN_PAGE_ROUTE,
  ACCOUNTING_PROJECT_TABLE_PAGE_ROUTE,
  ACCOUNTING_ADVANCES_PAGE_ROUTE,
  BANKING_PAGE_ROUTE,
  ORGANIZATION_PAGE_ROUTE,
  CHAT_PAGE_ROUTE,
  CHAT_ADMIN_PAGE_ROUTE,
  COMMUNITY_PAGE_ROUTE,
  REGISTRY_PROJECTS_PAGE_ROUTE,
  REGISTRY_ORGANIZATIONS_PAGE_ROUTE,
  REGISTRY_ORGANIZATION_PAGE_ROUTE,
  REGISTRY_PROJECT_PAGE_ROUTE,
  REGISTRY_PUBLICITY_PAGE_ROUTE,
} from "@/shared/utils";

export interface SupportSpot {
  id: string;
  /** Existing i18n key "Namespace:key". */
  labelKey: string;
  children?: SupportSpot[];
}

export interface SupportPageSpots {
  labelKey: string;
  spots: SupportSpot[];
}

// Shared by REQUESTS_PAGE_ROUTE / _ACTIVE / _HISTORY — all three render the
// same RequestsPage component. "active"/"history" details reuse both the same
// id and labelKey — uniqueness is asserted on the dotted flattened id
// ("active.details" vs "history.details"), so this is not a collision.
const REQUESTS_SPOTS: SupportSpot[] = [
  {
    id: "active",
    labelKey: "EmployeePortal:requests.view.active",
    children: [{ id: "details", labelKey: "EmployeePortal:requests.details.title" }],
  },
  {
    id: "history",
    labelKey: "EmployeePortal:requests.view.history",
    children: [{ id: "details", labelKey: "EmployeePortal:requests.details.title" }],
  },
  {
    id: "create",
    labelKey: "EmployeePortal:requests.createRequest",
    children: [
      { id: "annual_leave", labelKey: "EmployeePortal:requests.createAnnualLeave" },
      { id: "medical_leave", labelKey: "EmployeePortal:requests.createMedicalLeave" },
      { id: "amendment", labelKey: "EmployeePortal:requests.createAmendment" },
      { id: "termination", labelKey: "EmployeePortal:requests.createTermination" },
      { id: "item_request", labelKey: "EmployeePortal:requests.createItemRequest" },
      { id: "data_change", labelKey: "EmployeePortal:requests.createDataChange" },
      { id: "advance", labelKey: "EmployeePortal:advance.requestButton" },
    ],
  },
];

/** Route pattern → page label + the places inside it a user can point at.
 *  Labels are existing keys, so the map adds no translations. Pages without
 *  spots still list under "Section" with only "Other / whole page". */
export const SUPPORT_LOCATIONS: Record<string, SupportPageSpots> = {
  // --- HR ---
  [HR_EMPLOYEES_PAGE_ROUTE]: {
    labelKey: "EmployeesPage:header.title",
    spots: [
      { id: "add_employee", labelKey: "EmployeesPage:header.addWorker" },
      { id: "departments", labelKey: "EmployeesPage:departments.title" },
      { id: "staffing_table", labelKey: "EmployeesPage:staffingTable.title" },
      { id: "assign_role", labelKey: "EmployeesPage:roleAssignment.assignModal.title" },
    ],
  },
  [HR_IMPORT_WORKER_PAGE_ROUTE]: {
    labelKey: "ImportWorkerPage:header.title",
    spots: [
      { id: "personal", labelKey: "ImportWorkerPage:sections.personal" },
      { id: "contract", labelKey: "ImportWorkerPage:sections.contract" },
      { id: "banking", labelKey: "ImportWorkerPage:sections.banking" },
      { id: "documents", labelKey: "ImportWorkerPage:sections.documents" },
      { id: "save_draft", labelKey: "ImportWorkerPage:draft.saveDraft" },
    ],
  },
  [HR_WORKER_DETAIL_PAGE_ROUTE]: {
    labelKey: "WorkerDetailPage:breadcrumbs.employees",
    spots: [
      {
        id: "personal_sheet",
        labelKey: "WorkerDetailPage:tabs.personal_sheet",
        children: [{ id: "edit_personal_sheet", labelKey: "EmployeePortal:personalSheet.edit.button" }],
      },
      { id: "contract", labelKey: "WorkerDetailPage:tabs.contract" },
      { id: "documents", labelKey: "WorkerDetailPage:tabs.documents" },
      { id: "requests", labelKey: "WorkerDetailPage:tabs.requests" },
      { id: "business_trip", labelKey: "WorkerDetailPage:businessTrip.title" },
    ],
  },
  [PROJECTS_VOLUNTEERS_PAGE_ROUTE]: {
    labelKey: "VolunteersPage:header.title",
    spots: [
      { id: "create_volunteer", labelKey: "VolunteersPage:createModal.title" },
      { id: "assign_projects", labelKey: "VolunteersPage:assignProjectsModal.title" },
    ],
  },
  [HR_EXPERTS_PAGE_ROUTE]: {
    labelKey: "ExpertsPage:header.title",
    spots: [
      { id: "invite", labelKey: "ExpertsPage:inviteModal.title" },
      { id: "change_access", labelKey: "ExpertsPage:accessModal.title" },
      { id: "remove", labelKey: "ExpertsPage:actions.remove" },
    ],
  },
  [HR_HIRING_PAGE_ROUTE]: {
    labelKey: "HiringPage:header.title",
    spots: [
      { id: "new_invitation", labelKey: "HiringPage:invite.title" },
      { id: "application_details", labelKey: "HiringPage:modals.personalSheet" },
      { id: "revision", labelKey: "HiringPage:modals.revisionTitle" },
      { id: "reject", labelKey: "HiringPage:modals.rejectTitle" },
    ],
  },
  [HR_CONTRACTS_PAGE_ROUTE]: {
    labelKey: "ContractsPage:title",
    spots: [
      { id: "job_application_details", labelKey: "ContractsPage:jobApplication.title" },
      { id: "order_details", labelKey: "ContractsPage:order.title" },
    ],
  },
  [HR_LEAVES_PAGE_ROUTE]: {
    labelKey: "LeaveApplicationsPage:header.title",
    spots: [
      { id: "leave_schedule", labelKey: "LeaveApplicationsPage:schedule.title" },
      { id: "leave_details", labelKey: "LeaveApplicationsPage:details.title" },
      { id: "medical_leave_details", labelKey: "LeaveApplicationsPage:details.medicalLeave" },
      { id: "business_trip_details", labelKey: "LeaveApplicationsPage:businessTripDetails.title" },
    ],
  },
  [HR_FILL_CONTRACT_PAGE_ROUTE]: {
    labelKey: "FillContractPage:title",
    spots: [
      { id: "basic_info", labelKey: "FillContractPage:sections.basic_info" },
      { id: "position_duties", labelKey: "FillContractPage:sections.position_duties" },
      { id: "work_schedule", labelKey: "FillContractPage:sections.work_schedule" },
      { id: "preview", labelKey: "FillContractPage:preview.title" },
    ],
  },
  [HR_EDIT_CONTRACT_PAGE_ROUTE]: {
    labelKey: "FillContractPage:title_edit",
    spots: [
      { id: "basic_info", labelKey: "FillContractPage:sections.basic_info" },
      { id: "position_duties", labelKey: "FillContractPage:sections.position_duties" },
      { id: "work_schedule", labelKey: "FillContractPage:sections.work_schedule" },
      { id: "preview", labelKey: "FillContractPage:preview.title" },
    ],
  },

  // --- Legal ---
  [LEGAL_TEMPLATES_PAGE_ROUTE]: {
    labelKey: "LegalTemplatesPage:header.title",
    spots: [
      { id: "vehicle_rent", labelKey: "LegalTemplatesPage:forms.vehicleRent.title" },
      { id: "commercial_premise_rent", labelKey: "LegalTemplatesPage:forms.commercialPremiseRent.title" },
      { id: "service_contract", labelKey: "LegalTemplatesPage:forms.serviceContract.title" },
      { id: "paid_service", labelKey: "LegalTemplatesPage:forms.serviceAgreementMSB.title" },
      { id: "supply_contract", labelKey: "LegalTemplatesPage:forms.supplyContract.title" },
      { id: "custom_document", labelKey: "LegalTemplatesPage:forms.customDocument.title" },
      { id: "targeted_financing", labelKey: "LegalTemplatesPage:forms.targetedFinancing.title" },
    ],
  },
  [LEGAL_APPLICATIONS_PAGE_ROUTE]: {
    labelKey: "LegalApplicationsPage:header.title",
    spots: [{ id: "create_act", labelKey: "LegalApplicationsPage:cards.createAct" }],
  },
  [LEGAL_ATTACHED_DOCUMENTS_PAGE_ROUTE]: {
    labelKey: "AttachedDocumentsPage:title",
    spots: [
      { id: "upload", labelKey: "AttachedDocumentsPage:modals.create.title" },
      { id: "edit", labelKey: "AttachedDocumentsPage:modals.edit.title" },
      {
        id: "details",
        labelKey: "AttachedDocumentsPage:modals.details.title",
        children: [
          { id: "details_tab", labelKey: "AttachedDocumentsPage:modals.details.tabs.details" },
          { id: "history_tab", labelKey: "AttachedDocumentsPage:modals.details.tabs.history" },
        ],
      },
    ],
  },

  // --- Accounting: Timesheets & Payroll ---
  [ACCOUNTING_TIMESHEETS_PAGE_ROUTE]: {
    labelKey: "TimesheetsPage:title",
    spots: [
      { id: "create_timesheet", labelKey: "TimesheetsPage:generate.title" },
      { id: "download", labelKey: "TimesheetsPage:downloadModal.title" },
    ],
  },
  [ACCOUNTING_TIMESHEET_DETAIL_PAGE_ROUTE]: {
    labelKey: "TimesheetsPage:meta.pageTitle",
    spots: [
      { id: "step1", labelKey: "TimesheetsPage:steps.step1" },
      { id: "step2", labelKey: "TimesheetsPage:steps.step2" },
      {
        id: "step3",
        labelKey: "TimesheetsPage:steps.step3",
        children: [
          {
            id: "payroll_entry_detail",
            labelKey: "TimesheetsPage:payroll.detail.title",
            children: [
              { id: "accrual_tab", labelKey: "TimesheetsPage:payroll.tabs.accrual.title" },
              { id: "accounting_tab", labelKey: "TimesheetsPage:payroll.tabs.accounting.title" },
              { id: "payment_tab", labelKey: "TimesheetsPage:payroll.tabs.payment.title" },
              { id: "reporting_tab", labelKey: "TimesheetsPage:payroll.tabs.reporting.title" },
            ],
          },
        ],
      },
    ],
  },
  [ACCOUNTING_CONTRACTOR_PAYROLL_PAGE_ROUTE]: {
    labelKey: "ContractorPayrollPage:title",
    spots: [],
  },
  [ACCOUNTING_CONTRACTOR_PAYROLL_DETAIL_PAGE_ROUTE]: {
    labelKey: "ContractorPayrollPage:meta.pageTitle",
    spots: [
      {
        id: "gph_payment_details",
        labelKey: "ContractorPayrollPage:payroll.detail.gphPayment.title",
        children: [
          { id: "accrual_tab", labelKey: "ContractorPayrollPage:payroll.tabs.accrual.title" },
          { id: "accounting_tab", labelKey: "ContractorPayrollPage:payroll.tabs.accounting.title" },
          { id: "payment_tab", labelKey: "ContractorPayrollPage:payroll.tabs.payment.title" },
          { id: "reporting_tab", labelKey: "ContractorPayrollPage:payroll.tabs.reporting.title" },
        ],
      },
      { id: "approve_gph", labelKey: "ContractorPayrollPage:payroll.confirm.gphApproveTitle" },
      { id: "mark_paid_gph", labelKey: "ContractorPayrollPage:payroll.confirm.gphMarkPaidTitle" },
    ],
  },

  // --- Accounting: Purchases ---
  [ACCOUNTING_PURCHASES_PAGE_ROUTE]: {
    labelKey: "PurchasesPage:title",
    spots: [
      { id: "create_purchase", labelKey: "PurchasesPage:createModal.title" },
      {
        id: "purchase_details",
        labelKey: "PurchasesPage:detail.title",
        children: [
          { id: "calculation_tab", labelKey: "PurchasesPage:detail.tabs.calculation" },
          { id: "accounting_tab", labelKey: "PurchasesPage:detail.tabs.accounting" },
          { id: "payment_tab", labelKey: "PurchasesPage:detail.tabs.payment" },
          { id: "reporting_tab", labelKey: "PurchasesPage:detail.tabs.reporting" },
        ],
      },
    ],
  },

  // --- Accounting: Warehouse ---
  [ACCOUNTING_WAREHOUSE_REQUESTS_PAGE_ROUTE]: {
    labelKey: "WarehouseRequestsPage:title",
    spots: [
      { id: "details", labelKey: "WarehousePage:requests.detailModal.title" },
      { id: "fulfill", labelKey: "WarehousePage:requests.fulfillModal.title" },
      { id: "mark_unavailable", labelKey: "WarehousePage:requests.markUnavailableModal.title" },
    ],
  },
  [ACCOUNTING_DEPRECIATION_PAGE_ROUTE]: {
    labelKey: "DepreciationPage:title",
    spots: [],
  },
  [ACCOUNTING_FIXED_ASSETS_PAGE_ROUTE]: {
    labelKey: "FixedAssetsPage:title",
    spots: [
      { id: "assign", labelKey: "WarehousePage:assignModal.title" },
      { id: "details", labelKey: "WarehousePage:detailsModal.title" },
      { id: "transfer", labelKey: "WarehousePage:transferModal.title" },
      { id: "bulk_transfer", labelKey: "WarehousePage:bulkTransferModal.title" },
      { id: "revalue", labelKey: "WarehousePage:revalueModal.title" },
    ],
  },
  [ACCOUNTING_WAREHOUSE_PAGE_ROUTE]: {
    labelKey: "WarehousePage:title",
    spots: [
      { id: "assign", labelKey: "WarehousePage:assignModal.title" },
      { id: "details", labelKey: "WarehousePage:detailsModal.title" },
      { id: "transfer", labelKey: "WarehousePage:transferModal.title" },
      { id: "bulk_transfer", labelKey: "WarehousePage:bulkTransferModal.title" },
      { id: "revalue", labelKey: "WarehousePage:revalueModal.title" },
      { id: "batch_details", labelKey: "WarehousePage:batchDetailsModal.title" },
      { id: "batch_assign", labelKey: "WarehousePage:batchAssignModal.title" },
      { id: "batch_write_off", labelKey: "WarehousePage:batchWriteOffModal.title" },
    ],
  },

  // --- Accounting: Tax ---
  // Sub-tab keys (e.g. "tabs.cit.100.00") use literal dots — dropped, keep only the two main tabs.
  [ACCOUNTING_TAX_ROUTE]: {
    labelKey: "TaxPage:title",
    spots: [
      { id: "cit", labelKey: "TaxPage:tabs.main.cit" },
      { id: "payroll", labelKey: "TaxPage:tabs.main.payroll" },
    ],
  },

  // --- Accounting: Financial Reports ---
  [ACCOUNTING_FINANCIAL_REPORTS_PAGE_ROUTE]: {
    labelKey: "FinancialReportPage:title",
    spots: [
      { id: "download_pl", labelKey: "FinancialReportPage:downloadPLReport" },
      { id: "download_balance", labelKey: "FinancialReportPage:downloadBalanceSheet" },
      { id: "download_equity_changes", labelKey: "FinancialReportPage:downloadEquityChanges" },
    ],
  },
  [ACCOUNTING_EQUITY_CHANGES_REPORT_PAGE_ROUTE]: {
    labelKey: "FinancialReportPage:equityChanges.title",
    spots: [{ id: "download", labelKey: "FinancialReportPage:equityChanges.modal.downloadButton" }],
  },
  [ACCOUNTING_REPORTS_PAGE_ROUTE]: {
    labelKey: "ReportsPage:title",
    spots: [{ id: "journal_entry_detail", labelKey: "ReportsPage:journalEntries.detail.title" }],
  },

  // --- Accounting: Income ---
  [ACCOUNTING_INCOME_PAGE_ROUTE]: {
    labelKey: "IncomePage:title",
    spots: [
      { id: "create", labelKey: "IncomePage:modals.create.title" },
      { id: "edit", labelKey: "IncomePage:modals.edit.title" },
      {
        id: "details",
        labelKey: "IncomePage:modals.details.title",
        children: [{ id: "founder_details", labelKey: "IncomePage:modals.founderDetails.title" }],
      },
    ],
  },

  // --- Finance ---
  [FINANCE_PAGE_ROUTE]: {
    labelKey: "FinancePage:page.title",
    spots: [
      { id: "export", labelKey: "FinancePage:export.button" },
      { id: "redistribute", labelKey: "FinancePage:redistribute.button" },
    ],
  },
  [ACCOUNTING_PROJECT_TABLE_PAGE_ROUTE]: {
    labelKey: "ProjectTablePage:header.title",
    spots: [],
  },
  [ACCOUNTING_ADVANCES_PAGE_ROUTE]: {
    labelKey: "AdvancesPage:header.title",
    spots: [{ id: "details", labelKey: "AdvancesPage:detail.title" }],
  },

  // --- Self-service ---
  [PROFILE_PAGE_ROUTE]: {
    labelKey: "EmployeePortal:meta.profileTitle",
    spots: [
      { id: "personal_sheet", labelKey: "EmployeePortal:personalSheet.tabs.personalSheet" },
      { id: "work_info", labelKey: "EmployeePortal:personalSheet.tabs.workInfo" },
      { id: "documents", labelKey: "EmployeePortal:personalSheet.tabs.documents" },
      { id: "edit", labelKey: "EmployeePortal:personalSheet.edit.button" },
    ],
  },
  [REQUESTS_PAGE_ROUTE]: { labelKey: "EmployeePortal:requests.title", spots: REQUESTS_SPOTS },
  [REQUESTS_ACTIVE_PAGE_ROUTE]: { labelKey: "EmployeePortal:requests.title", spots: REQUESTS_SPOTS },
  [REQUESTS_HISTORY_PAGE_ROUTE]: { labelKey: "EmployeePortal:requests.title", spots: REQUESTS_SPOTS },
  [CALCULATIONS_PAGE_ROUTE]: {
    labelKey: "EmployeePortal:calculations.title",
    spots: [
      { id: "create_act", labelKey: "EmployeePortal:calculations.modal.title" },
      { id: "edit_act", labelKey: "EmployeePortal:calculations.modal.editTitle" },
    ],
  },
  [PENDING_ACTIONS_PAGE_ROUTE]: {
    labelKey: "PendingActionsPage:title",
    spots: [],
  },

  // --- Support ---
  [SUPPORT_PAGE_ROUTE]: {
    labelKey: "SupportPage:title",
    spots: [],
  },

  // --- Projects ---
  [PROJECTS_PAGE_ROUTE]: {
    labelKey: "ProjectsPage:page.title",
    spots: [
      {
        id: "create",
        labelKey: "ProjectsPage:page.createProject",
        children: [{ id: "form", labelKey: "ProjectsPage:projectForm.titleCreate" }],
      },
      { id: "edit", labelKey: "ProjectsPage:projectForm.titleEditInternal" },
      { id: "removal", labelKey: "ProjectsPage:removal.modalTitle" },
    ],
  },
  [PROJECT_MONITORING_ROUTE]: {
    labelKey: "ProjectMonitoringPage:header.title",
    spots: [],
  },
  [PROJECT_DETAIL_ROUTE]: {
    labelKey: "ProjectsPage:page.detailTitle",
    spots: [
      { id: "members", labelKey: "Common:members.title" },
      { id: "events", labelKey: "ProjectsPage:events.form.createTitle" },
      { id: "publications", labelKey: "ProjectsPage:publications.form.createTitle" },
    ],
  },

  // --- Reporting: project / annual / ESG previews ---
  [PROJECT_REPORT_PREVIEW_ROUTE]: {
    labelKey: "ProjectsPage:reports.preview.title",
    spots: [
      { id: "download", labelKey: "ProjectsPage:reports.preview.downloadPdf" },
      { id: "submit", labelKey: "ProjectsPage:reports.actions.submit" },
      { id: "reopen", labelKey: "ProjectsPage:reports.actions.reopen" },
    ],
  },
  [PROJECT_REPORTS_ROUTE]: {
    labelKey: "ProjectsPage:reports.listTitle",
    spots: [{ id: "wizard", labelKey: "ProjectsPage:reports.wizard.title" }],
  },
  [ANNUAL_REPORT_PREVIEW_ROUTE]: {
    labelKey: "ProjectsPage:reports.annual.preview.title",
    spots: [{ id: "download", labelKey: "ProjectsPage:reports.annual.preview.downloadPdf" }],
  },
  [ANNUAL_REPORTS_ROUTE]: {
    labelKey: "ProjectsPage:reports.annual.listTitle",
    spots: [
      {
        id: "create",
        labelKey: "ProjectsPage:reports.annual.actions.create",
        children: [{ id: "wizard", labelKey: "ProjectsPage:reports.annual.wizard.title" }],
      },
    ],
  },
  [ESG_REPORT_PREVIEW_ROUTE]: {
    labelKey: "ReportingPage:esg.preview.title",
    spots: [{ id: "download", labelKey: "ReportingPage:esg.preview.downloadPdf" }],
  },
  [ESG_REPORTS_ROUTE]: {
    labelKey: "ReportingPage:esg.title",
    spots: [
      {
        id: "create",
        labelKey: "ReportingPage:esg.actions.create",
        children: [{ id: "wizard", labelKey: "ReportingPage:esg.wizard.title" }],
      },
    ],
  },

  // --- SROI ---
  [SROI_PROJECT_ROUTE]: {
    labelKey: "SroiPage:projectPage.meta.title",
    spots: [
      { id: "configure", labelKey: "SroiPage:projectPage.tabs.configure" },
      { id: "execution", labelKey: "SroiPage:projectPage.tabs.execution" },
      {
        id: "social_result",
        labelKey: "SroiPage:projectPage.tabs.socialResult",
        children: [{ id: "create_campaign", labelKey: "SroiPage:social.campaigns.newCampaign" }],
      },
      {
        id: "sroi",
        labelKey: "SroiPage:projectPage.tabs.sroi",
        children: [{ id: "assessment_detail", labelKey: "SroiPage:sroi.history.detailTitle" }],
      },
    ],
  },
  [SROI_ROUTE]: {
    labelKey: "SroiPage:projectsList.title",
    spots: [],
  },

  // --- Forms ---
  [FORMS_PAGE_ROUTE]: {
    labelKey: "FormsLibraryPage:page.title",
    spots: [],
  },
  [CAMPAIGNS_PAGE_ROUTE]: {
    labelKey: "CampaignsPage:page.title",
    spots: [],
  },
  [CAMPAIGN_PAGE_ROUTE]: {
    labelKey: "CampaignsPage:page.fallbackTitle",
    spots: [],
  },

  // --- Board ---
  [BOARD_PAGE_ROUTE]: {
    labelKey: "BoardPage:header.title",
    spots: [
      { id: "overview", labelKey: "BoardPage:tabs.overview" },
      {
        id: "kanban",
        labelKey: "BoardPage:tabs.kanban",
        children: [{ id: "create_task", labelKey: "TasksPage:taskForm.titleCreate" }],
      },
      {
        id: "list",
        labelKey: "BoardPage:tabs.list",
        children: [{ id: "backlog", labelKey: "TasksPage:backlog.modalTitle" }],
      },
    ],
  },

  // --- Banking ---
  [BANKING_PAGE_ROUTE]: {
    labelKey: "BankingPage:page.title",
    spots: [],
  },

  // --- Organization ---
  [ORGANIZATION_PAGE_ROUTE]: {
    labelKey: "MyOrganizationPage:page.title",
    spots: [],
  },

  // --- Chat ---
  [CHAT_PAGE_ROUTE]: {
    labelKey: "ChatPage:title",
    spots: [
      {
        id: "manage_channels",
        labelKey: "ChatPage:manageChannels",
        children: [{ id: "modal", labelKey: "ChatPage:manage.title" }],
      },
    ],
  },
  [CHAT_ADMIN_PAGE_ROUTE]: {
    labelKey: "ChatAdminPage:title",
    spots: [],
  },

  // --- Community ---
  [COMMUNITY_PAGE_ROUTE]: {
    labelKey: "CommunityPage:header.title",
    spots: [
      { id: "feed", labelKey: "CommunityPage:tabs.feed" },
      { id: "events", labelKey: "CommunityPage:tabs.events" },
      { id: "publish", labelKey: "CommunityPage:header.publish" },
      { id: "reactions", labelKey: "CommunityPage:support.reactions" },
    ],
  },

  [REGISTRY_PROJECTS_PAGE_ROUTE]: {
    labelKey: "RegistryPage:header.projects",
    spots: [],
  },
  [REGISTRY_ORGANIZATIONS_PAGE_ROUTE]: {
    labelKey: "RegistryPage:header.organizations",
    spots: [],
  },
  [REGISTRY_ORGANIZATION_PAGE_ROUTE]: {
    labelKey: "RegistryPage:breadcrumbs.organization",
    spots: [],
  },
  [REGISTRY_PROJECT_PAGE_ROUTE]: {
    labelKey: "RegistryPage:breadcrumbs.project",
    spots: [],
  },
  [REGISTRY_PUBLICITY_PAGE_ROUTE]: {
    labelKey: "RegistryPage:publicity.title",
    spots: [],
  },
};
